import { clearModuleRefusal, noteModuleRefusal } from '@/libs/license'

/**
 * The portal's one way of talking to the hotel.
 *
 * # Why this file exists here and not as a store object
 *
 * `vue-spec/libraries.md` puts the transport in `stores/fetch.ts`, and this is it. It
 * is not a Pinia store with a `createFetch` inside, the way the reception portal's is,
 * and that is deliberate rather than unfinished: the reception portal authenticates
 * with a session in a header, so its transport has to reach a store for the token and
 * a router for the sign-in screen. This portal has no session and no sign-in — every
 * request carries a token IN THE PATH, from the link the hotel sent — so a store would
 * have nothing to hold, and a composable that must be called from a setup context
 * would be harder to use from the router guard that also needs it.
 *
 * What matters is the rule the spec is protecting: there is one place that talks to
 * the hotel, and screens do not open connections of their own.
 *
 * The token lives in sessionStorage: closing the browser forgets it, which is the
 * right default for a device that may be borrowed, and the link in the guest's mail
 * opens it again.
 */
const TOKEN_KEY = 'guest.token'

/**
 * Remembers the token a link carried, and forgets a refusal that belonged to another
 * one.
 *
 * Every kind of link this portal serves — a stay, an offer, a rooming form — stashes
 * its token through here, so the one rule lives in one place. A DIFFERENT token is a
 * different guest and a different hotel, and whatever the API said about the last one
 * says nothing about this one. The same token stashed again changes nothing: opening
 * the same link twice is the same question, and asking it again would be the retry
 * this portal deliberately does not do.
 */
export const stashToken = (key: string, token: string) => {
    if (token && token !== sessionStorage.getItem(key)) clearModuleRefusal()

    sessionStorage.setItem(key, token)
}

export const setToken = (token: string) => stashToken(TOKEN_KEY, token)
export const getToken = (): string => sessionStorage.getItem(TOKEN_KEY) ?? ''
export const hasToken = (): boolean => !!getToken()

export interface ApiAnswer<T> {
    status: number
    data:   T | null
}

/**
 * How one call is read, beside the request itself.
 *
 * `optional` is a page that is ONE part of the stay under a licence of its own — room
 * service under the till's. Its refusal for a module the house has not bought means that
 * one page has nothing to show, and must not turn the whole portal into the „this hotel
 * does not offer this“ screen, which is what the same refusal means on the stay itself.
 * The page reads the 403 and says so in its own words.
 */
export interface ApiOptions {
    optional?: boolean
}

/** A call on the guest's own stay, with their token in front of the path. */
export const api = async <T>(path = '', options: RequestInit = {}, read: ApiOptions = {}): Promise<ApiAnswer<T>> => {
    const token = getToken()

    if (!token) return { status: 0, data: null }

    return publicApi<T>(`/guest/${token}${path}`, options, read)
}

/**
 * The same request with the address given whole.
 *
 * The portal serves more than one kind of link — a stay, an offer, a rooming form —
 * and each carries its own token in its own place in the path.
 *
 * The answer is a status and a body, never a throw: a page opened on a telephone in a
 * car park has to render something when the network is not there.
 */
export const publicApi = async <T>(path: string, options: RequestInit = {}, read: ApiOptions = {}): Promise<ApiAnswer<T>> => {
    try {
        const response = await fetch(`/api/pms/v1${path}`, {
            headers: { 'Content-Type': 'application/json' },
            ...options,
        })

        const body = await response.json().catch(() => null)

        // The one place every call passes through, so the hotel not having bought this
        // module is noticed once rather than screen by screen. Read before the answer
        // is handed back: a 403 with this code is not a dead link and must not be
        // shown as one.
        if (!read.optional) noteModuleRefusal(response.status, body)

        return { status: response.status, data: body as T }
    } catch {
        return { status: 0, data: null }
    }
}
